import React, { useState, useEffect } from 'react';

import { posApi } from '@/services/api';
import { Loader2, ArrowRightLeft, X, AlertTriangle } from 'lucide-react';
import { CurrencyIcon } from '@/components/common/CurrencyIcon';
import toast from 'react-hot-toast';

import { formatMoney } from '@/utils/currency';

interface BankAccount {
  name: string;
  bank_name: string;
  account_title: string;
  current_balance: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  banks: BankAccount[];
  onSuccess?: () => void;
}

export default function BankTransferModal({ open, onClose, banks, onSuccess }: Props) {
  const [fromBank, setFromBank] = useState('');
  const [toBank, setToBank] = useState('');
  const [amount, setAmount] = useState('');
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setFromBank('');
      setToBank('');
      setAmount('');
      setRemarks('');
      setError('');
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!fromBank || !toBank) {
      setError('Please select both source and destination banks');
      return;
    }
    if (fromBank === toBank) {
      setError('Source and destination banks must be different');
      return;
    }

    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setSubmitting(true);
    try {
      await posApi.createBankTransfer({
        from_bank: fromBank,
        to_bank: toBank,
        amount: amt,
        remarks,
      });
      toast.success('Transfer successful');
      onSuccess?.();
      onClose();
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to transfer funds';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden animate-fade-in">
        <div className="flex items-start justify-between gap-3 px-6 py-5 border-b shrink-0 bg-blue-50 border-blue-100">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2.5 rounded-xl shrink-0 bg-blue-100 text-blue-700">
              <ArrowRightLeft className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-lg text-blue-950">Bank Transfer</h3>
              <p className="text-xs mt-0.5 text-blue-700/80">Transfer funds between your bank accounts</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-lg shrink-0 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Bank</label>
              <select
                className="input-field bg-white"
                value={fromBank}
                onChange={(e) => setFromBank(e.target.value)}
                required
              >
                <option value="">Select source bank...</option>
                {banks
                  .filter((b) => b.name !== toBank)
                  .map((b) => (
                    <option key={b.name} value={b.name}>
                      {b.bank_name} {b.account_title && b.account_title !== b.bank_name ? `- ${b.account_title}` : ''} (Bal: {formatMoney(b.current_balance)})
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Bank</label>
              <select
                className="input-field bg-white"
                value={toBank}
                onChange={(e) => setToBank(e.target.value)}
                required
              >
                <option value="">Select destination bank...</option>
                {banks
                  .filter((b) => b.name !== fromBank)
                  .map((b) => (
                    <option key={b.name} value={b.name}>
                      {b.bank_name} {b.account_title && b.account_title !== b.bank_name ? `- ${b.account_title}` : ''} (Bal: {formatMoney(b.current_balance)})
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
              <div className="relative">
                <CurrencyIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="input-field pl-9 font-mono"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Remarks (Optional)</label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                className="input-field min-h-[80px] resize-none"
                placeholder="E.g. Internal funds transfer"
              />
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 p-3.5 rounded-xl text-sm flex items-start gap-2 border border-red-100">
                <AlertTriangle className="w-5 h-5 shrink-0 text-red-500" />
                <span>{error}</span>
              </div>
            )}
          </div>

          <div className="shrink-0 border-t border-gray-100 bg-gray-50 p-4 flex gap-3">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-2.5 rounded-lg font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 focus-visible:ring-blue-500"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Transfer Funds
            </button>
            <button type="button" onClick={onClose} className="btn-secondary py-2.5">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
